import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Toast as T } from "radix-ui";
import { CircleAlert, CircleCheck, Info, X } from "lucide-react";
import { cn } from "../../lib/cn";
import { useKitCopy } from "../../lib/kit-copy";
import { durationMs } from "../../tokens";

type Tone = "success" | "info" | "error";
interface ToastItem {
  id: number;
  tone: Tone;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}
const Ctx = createContext<(t: Omit<ToastItem, "id">) => void>(() => undefined);
export const useToast = () => useContext(Ctx);

/**
 * Toasts for async events (scan finished, report ready). Radix Toast: swipe, F8 to focus, polite live region.
 * Place it inside `KitCopyProvider` so its close button speaks the page's language.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const l = useKitCopy().labels;
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: Omit<ToastItem, "id">) => setItems((s) => [...s, { ...t, id: Date.now() + Math.random() }]), []);
  return (
    <Ctx.Provider value={push}>
      <T.Provider swipeDirection="right" duration={durationMs.toast}>
        {children}
        {items.map((t) => {
          const Icon = t.tone === "success" ? CircleCheck : t.tone === "error" ? CircleAlert : Info;
          return (
            <T.Root
              key={t.id}
              type={t.tone === "error" ? "foreground" : "background"}
              onOpenChange={(o) => !o && setItems((s) => s.filter((x) => x.id !== t.id))}
              className="flex items-start gap-3 rounded-card border border-border bg-surface p-4 shadow-overlay"
            >
              <Icon aria-hidden className={cn("mt-0.5 size-5 shrink-0", t.tone === "success" && "text-ok", t.tone === "error" && "text-exploited", t.tone === "info" && "text-low")} />
              <div className="min-w-0 flex-1">
                <T.Title className="text-sm font-semibold">{t.title}</T.Title>
                {t.description && <T.Description className="text-xs text-text-muted">{t.description}</T.Description>}
                {t.action && (
                  <T.Action altText={t.action.label} asChild>
                    <button type="button" onClick={t.action.onClick} className="mt-2 inline-flex min-h-touch items-center text-xs font-medium text-brand-600 hover:underline lg:min-h-0">
                      {t.action.label}
                    </button>
                  </T.Action>
                )}
              </div>
              <T.Close aria-label={l.dismiss} className="-m-2 inline-grid size-touch place-items-center rounded-btn text-text-muted hover:bg-surface-2 lg:size-8">
                <X aria-hidden className="size-4" />
              </T.Close>
            </T.Root>
          );
        })}
        <T.Viewport className="fixed bottom-4 right-4 z-(--z-toast) flex w-full max-w-sm flex-col gap-2 outline-none" />
      </T.Provider>
    </Ctx.Provider>
  );
}
